import React, { useEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useAdminBatchSocket from '~/hooks/useAdminBatchSocket';

// Actions
import {
  fetchAdminBatchFilters,
  fetchAdminBatches,
  patchAdminBatchProgress,
  patchAdminBatchStatuses,
} from '~/redux/actions/admin-batch-actions';
import { CLEAR_ADMIN_BATCHES_FILTERS } from '~/redux/actions/action-types';

//Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { balanceFormatter, debounce, numberWithComma } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import AdvanceFilters from './Filters';
import NoBatchesScreen from './NoCategoriesScreen';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Table
import { Box, Grid, Typography, useMediaQuery, Container } from '@mui/material';

// Icons
import { OrderCIcon, RevenueCIcon } from '~/assets/images';

// Styles
import './styles.scss';

// Live updates arrive over a socket; this refresh is only the safety net for events that never came
const SAFETY_REFRESH_CONNECTED_MS = 60 * 1000;
const SAFETY_REFRESH_DISCONNECTED_MS = 30 * 1000;
// A burst of status changes (many batches at once) becomes one refetch
const STATUS_CHANGED_REFRESH_DELAY_MS = 1000;

const AdminBatch = () => {
  const { batchId } = useParams();
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);

  const childRef = useRef();
  const dispatch = useDispatch();

  const applyFilters = useSelector((state) => state.adminBatchesReducers.applyFilters);

  const filters = useSelector((state) => state.adminBatchesReducers.filters);
  const filterSize = useSelector((state) => state.adminBatchesReducers.filterSize);

  const totalQuantity = useSelector((state) => state.adminBatchesReducers.totalQuantity);

  const totalRevenue = useSelector((state) => state.adminBatchesReducers.totalRevenue);

  const totalBatchesInDB = useSelector((state) => state.adminBatchesReducers.totalBatchesInDB);

  const statsCards = [
    { id: 'quantity', label: 'Total Quantity', value: numberWithComma(totalQuantity), icon: OrderCIcon },
    {
      id: 'revenue',
      label: 'Total Revenue',
      value: balanceFormatter(totalRevenue),
      icon: RevenueCIcon,
    },
  ];

  const handleSearch = () => {
    if (search) {
      search.trim();
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getAdminBatches();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getAdminBatches = (batchId) => {
    let payload = {
      pagination,
      search: batchId ? batchId : search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchAdminBatches(payload));
  };

  // Background refresh of the current page (same page, search and filters), without the grid loading overlay.
  // Kept in a ref so the socket handlers and timers below always see the latest state.
  const refreshRef = useRef(() => {});
  refreshRef.current = () => {
    dispatch(
      fetchAdminBatches({
        pagination,
        search: searchApplied && search.length ? search : null,
        applyFilters,
        filters: applyFilters ? filters : null,
        silent: true,
      })
    );
  };
  const scheduleRefresh = useMemo(
    () => debounce(() => refreshRef.current(), STATUS_CHANGED_REFRESH_DELAY_MS),
    []
  );

  const { connected: liveConnected } = useAdminBatchSocket({
    onProgress: (snapshot) => dispatch(patchAdminBatchProgress(snapshot)),
    onStatusChanged: (payload) => {
      // The server sends the new status along when olc-batching-server wrote it: show it at once, then refetch the row
      if (payload?.changes?.length) dispatch(patchAdminBatchStatuses(payload.changes));
      scheduleRefresh();
    },
    onReconnect: scheduleRefresh,
  });

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') refreshRef.current();
    };
    const intervalId = setInterval(
      refreshIfVisible,
      liveConnected ? SAFETY_REFRESH_CONNECTED_MS : SAFETY_REFRESH_DISCONNECTED_MS
    );
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [liveConnected]);

  useEffect(() => {
    setIsMounted(true);
    batchId && setSearch(batchId);
    batchId && setSearchApplied(true);
    batchId ? getAdminBatches(batchId) : getAdminBatches();
    dispatch(fetchAdminBatchFilters());
    return () => {
      dispatch({ type: CLEAR_ADMIN_BATCHES_FILTERS });
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      getAdminBatches();
    }
  }, [pagination.page, pagination.pageSize, filterSize, filters]);

  useEffect(() => {
    if (!search && isMounted) {
      setSearchApplied(false);
      setSearch('');
      getAdminBatches();
    }
  }, [search]);

  const noBatchesInDb = () => {
    return (
      <Container maxWidth={maxWidth} className="olcAdminEmptyPage">
        <NoBatchesScreen />
      </Container>
    );
  };

  if (totalBatchesInDB === 0 && totalBatchesInDB !== null) {
    return noBatchesInDb();
  }

  return (
    <>
      <Container maxWidth="xxl" className="olcAdminListPage">
        <div className="adminOrderMain olcAdminListContent">
          <Grid container className="olcAdminListToolbar olcAdminListToolbar--split">
            <Grid item lg={6} md={6} sm={12} xs={12} className="searchBtnWrapper">
              <Box className="searchWrapper">
                <OlcSearchInput
                  type="text"
                  value={search}
                  searchApplied={searchApplied}
                  onChange={(e) => {
                    if (e.target.value.length > 9) return;
                    setSearch(e.target.value.replace(/\s/g, '').replace(/\D/g, ''));
                  }}
                  onKeyDown={debouncedHandleKeyDown}
                  placeholder={MESSAGES.ADMIN_DASHBOARD.BATCHES.SEARCH_PLACE_HOLDER}
                  onClear={() => setSearch('')}
                  inputProps={{
                    'aria-label': MESSAGES.ADMIN_DASHBOARD.BATCHES.SEARCH_PLACE_HOLDER,
                    pattern: '[0-9]*',
                    title: 'Only numbers are allowed',
                  }}
                />
                <Box className="filterBtnWrapper">
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      setOpenAdvanceFilters(true);
                    }}
                  >
                    Filters
                  </OlcButton>
                  {filterSize > 0 && (
                    <Typography className="filtersCounter">{filterSize}</Typography>
                  )}
                </Box>
                {filterSize > 0 && (
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      childRef.current.clearFilters();
                    }}
                    className="clearFilterBtn"
                  >
                    {MESSAGES.ADMIN_DASHBOARD.BATCHES.CLEAR_FILTER_BTN_TEXT}
                  </OlcButton>
                )}
              </Box>
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12} className="searchBtnWrapper outer">
              {statsCards.map((item) => (
                <Box
                  key={item.id}
                  className="infoBox olcAdminStatCard"
                  aria-label={`${item.label}: ${item.value}`}
                >
                  <img src={item.icon} alt="" aria-hidden="true" className="infoBoxIcon" />
                  <p className="infoBoxValue">{item.value}</p>
                </Box>
              ))}
            </Grid>
          </Grid>
          <Table
            pagination={pagination}
            updatePagination={setPagination}
            searchApplied={searchApplied}
          />
          <AdvanceFilters
            open={openAdvanceFilters}
            handleCloseFilter={() => setOpenAdvanceFilters(false)}
            ref={childRef}
            updatePagination={setPagination}
          />
        </div>
      </Container>
    </>
  );
};

export default AdminBatch;
